import { useCallback, useRef, useState } from 'react';
import { ExternalLink, File, FileText } from 'lucide-react';

import {
    buildDownloadsClickExtraData,
    useTracker,
} from '@archipro-website/tracker';
import { formatFileSize } from '@modules/build-hub/utils/format-document-attributes';
import { useGatedDownloads } from '@modules/product/hook/use-gated-downloads';
import { DownloadSurface } from 'generated/graphql';
import { DownloadFileRow } from '~/ui-rocco/pages/detail-shared';
import type { RevampDownload } from '../type';

/**
 * Articles have no file categories, so the column that names the file's kind
 * on the product page reads the same for every article file.
 */
const FILE_LABEL = 'Files';

/** Uploader shown when the article credits no professional. */
const ARCHIPRO = 'ArchiPro';

interface RevampDownloadRowProps {
    downloads: RevampDownload[];
    /** Article the files hang off, sent with the download event. */
    articleID: number;
    /** Credited professional, used as the event label and the uploader. */
    professionalTitle?: string;
    /**
     * Credited professional's id. A download is a lead, so without this it is
     * only counted site-wide and never towards that client's advertising
     * conversions.
     */
    professionalID?: number;
}

interface ArticleFileRowProps {
    download: RevampDownload;
    articleID: number;
    uploader: string;
    professionalTitle?: string;
    professionalID?: number;
    /** The article's gate, shared by its files so they cost one lead. */
    gate: ArticleGate;
}

interface ArticleGate {
    isGated: boolean;
    isLoggedIn: boolean;
    onGatedDownload: (link: string) => void;
}

/**
 * One article file in the shared detail-page download row, behind the same gate
 * every other ArchiPro download sits behind: an account, and the lead that
 * taking the file earns the credited professional. An article ArchiPro wrote
 * itself credits nobody, so it asks for the account and records no lead.
 */
const ArticleFileRow = ({
    download,
    articleID,
    uploader,
    professionalTitle,
    professionalID,
    gate,
}: ArticleFileRowProps) => {
    const tracking = useTracker();
    const hasBeenTracked = useRef(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);

    const handleDownload = useCallback(() => {
        if (!download.link) return;

        if (!hasBeenTracked.current) {
            hasBeenTracked.current = true;
            tracking.log(
                gate.isLoggedIn ? 'DownloadsClick' : 'DownloadsLoggedOutClick',
                {
                    url: new URL(window.location.href),
                    label: professionalTitle ?? '',
                    professionalId: professionalID,
                    data: {
                        ExtraData: buildDownloadsClickExtraData({
                            fileTitle: download.title,
                            fileUrl: download.link,
                        }),
                        articleID,
                        professionalID,
                        fileTitle: download.title,
                        fileID: download.id,
                        downloadGated: gate.isGated,
                    },
                },
            );
        }

        // The gate owns the file from here: the account first, then the file,
        // then the lead the credited professional earns from it.
        if (gate.isGated) {
            gate.onGatedDownload(download.link);
            // No tick yet. The gate hands the file over from its own success
            // step, and a reader who abandons auth never gets it — a row that
            // says "downloaded" then is telling them something untrue.
            return;
        }

        window.open(download.link, '_blank', 'noopener');
        setHasDownloaded(true);
    }, [
        download,
        articleID,
        professionalTitle,
        professionalID,
        tracking,
        gate,
    ]);

    const Icon = download.isExternal
        ? ExternalLink
        : download.extension
          ? FileText
          : File;
    const format = download.isExternal
        ? 'Link'
        : (download.extension?.toUpperCase() ?? 'File');
    // External links have no server-side size, so they fall back to a dash.
    const size = download.sizeBytes ? formatFileSize(download.sizeBytes) : '—';

    return (
        <DownloadFileRow
            title={download.title}
            uploader={uploader}
            fileLabel={FILE_LABEL}
            format={format}
            size={size}
            Icon={Icon}
            hasDownloaded={hasDownloaded}
            onDownload={handleDownload}
        />
    );
};

/**
 * Content of the article's download accordion row: every file attached to the
 * article, in the same row the product page uses.
 */
export const RevampDownloadRow = ({
    downloads,
    articleID,
    professionalTitle,
    professionalID,
}: RevampDownloadRowProps) => {
    // One gate for the article, not one per file: the lead its files earn is
    // recorded once. An article ArchiPro wrote itself credits no professional,
    // and `professionalID` 0 is how the gate is told there is no lead to file.
    const { isGated, isLoggedIn, handleGatedDownload } = useGatedDownloads({
        surface: DownloadSurface.ArticleFiles,
        professionalID: professionalID ?? 0,
        libraryItemID: articleID,
    });

    if (downloads.length === 0) return null;

    const uploader = professionalTitle ?? ARCHIPRO;
    const gate: ArticleGate = {
        isGated,
        isLoggedIn,
        onGatedDownload: handleGatedDownload,
    };

    return (
        <div className="pb-[1.8rem] max-md:pb-4 md:space-y-[0.1875rem]">
            {downloads.map((download) => (
                <ArticleFileRow
                    key={download.id}
                    download={download}
                    articleID={articleID}
                    uploader={uploader}
                    professionalTitle={professionalTitle}
                    professionalID={professionalID}
                    gate={gate}
                />
            ))}
        </div>
    );
};
